import React, { useState, useRef, useEffect } from 'react';
import { useUserAgent } from 'next-useragent';
import nookies, { setCookie } from 'nookies';
import { NextSeo } from 'next-seo';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { apiProxy } from '../../lib/api';
import { RootStoreProvider } from '../../providers/RootStoreProvider';
import { Features } from '../../components/Features';
import { Collapsed } from '../../components/Collapsed';
import { Layout } from '../../components/Layout';
import { Text, Anchor } from '../../components/Text';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { declOfNum } from '../../utils/declOfNum';
import { FilterShips } from '../../components/ShipsPage/FilterShips';
import { hasSeo, seoData } from '../../utils/seo';
import { serialize } from '../../utils/serialize';
import { Reviews } from '../../components/Reviews';
import { scrollToUtil } from '../../utils/scrollToUtil';
import { PricePanel } from '../../components/CruisePageComponents/PricePanel';
import styles from '../../styles/shipPage.module.sass';
import { Badge } from '../../components/Badge';
import { SearchStoreProvider } from '../../providers/SearchStoreProvider';
import { MainGallery } from '../../components/CruisePageComponents/MainGallery';
import { ShipInfo } from '../../components/ShipInfo';
import { YandexLink } from '../../components/YandexLink';
import { YaAndGaShipPage } from '../../lib/YaAndGaHelper';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { redirectInvalidUrlShips } from '../../resources/redirectInvalidUrl';
import { ModalReload } from '../../components/ModalReload';
import { yandexDataLayer, yandexUserLayer } from '../../lib/yandexDataLayer';
import { redirectFromTo } from '../../utils/redirectFromTo';
import { useHiddenWithCookie } from '../../hooks/useHiddenWithCookie';
import { logger } from '../../lib/logger';
import { sendVKPixelEvent } from '../../lib/sendVKPixelEvent';

const BannerTop = dynamic(() =>
  import('../../components/CookieBanner').then(mod => mod.CookieBanner),
);

export default function Ship({
  uaString,
  drawInfo,
  drawCruise,
  seo,
  store: { user, cookies, popularPopdirs },
  canonicalUrl,
}) {
  const ua = useUserAgent(uaString || window.navigator.userAgent);
  const { isMobile, isTablet } = ua;

  const isSea = drawInfo?.ship_direction === 'sea';

  const shipDetails = {
    id: drawInfo.id,
    name: drawInfo.t_title,
    brand: drawInfo.company,
    category: `${drawInfo.ship_direction === 'sea' ? 'Море' : 'Река'}/${
      drawInfo.category_name
    }/${drawInfo.t_title}`,
    price: drawInfo.kr_min_price,
    quantity: 1,
  };

  useEffect(() => {
    // Яндекс метрика - информация о пользователи и типе страницы
    yandexUserLayer(user?.user?.id, user?.user?.group, isSea);
    YaAndGaShipPage(isSea ? 'isSeaPage' : 'isRiverPage');
  }, [isSea]);

  useEffect(() => {
    yandexDataLayer('detail', shipDetails);
    sendVKPixelEvent('viewProduct', {
      product_id: drawInfo.id,
      value: drawInfo.kr_min_price,
    });
  }, []);

  const shipsInfoTitle =
    drawInfo?.id === 410 || drawInfo?.id === 469 || drawInfo?.id === 468
      ? 410
      : isSea;

  const shipType = (nameType, isRouter) => {
    switch ((nameType, isRouter)) {
      case nameType === 410 && 'isTitle':
        return 'Яхта';
      case nameType === 410 && 'isRouter':
        return 'яхте';
      case nameType === 410 && 'isButton':
        return 'яхты';
      case nameType === true && 'isTitleSeo':
        return 'лайнер';
      case nameType === true && 'isTitle':
        return 'Лайнер';
      case nameType === true && 'isRouter':
        return 'лайнере';
      case nameType === true && 'isButton':
        return 'лайнера';
      case nameType === false && 'isTitle':
        return 'Теплоход';
      case nameType === false && 'isRouter':
        return 'теплоходе';
      case nameType === false && 'isButton':
        return 'теплохода';
      default:
        return nameType;
    }
  };

  const arrayYear = drawInfo.navigation_years;

  Object.freeze(arrayYear);

  const tabsYear = arrayYear.map(i => ({
    title: `Круизы на ${i} год`,
    value: i,
  }));
  const [clipp, setClipp] = useState();

  const minPrice = isSea
    ? drawInfo.kr_min_price
    : drawInfo.kr_min_price_discounted;

  const meta = {
    title: `${shipType(shipsInfoTitle, 'isTitle')} ${
      seo.t_title
    } – навигация  ${
      drawInfo.year
    }, расписание и цены круизов, отзывы туристов, фото кают и палубы`,
    description: `Выберете для себя круиз на теплоходе ${seo.t_title} в навигации ${drawInfo.year}: актуальное расписание и цены, реальные отзывы туристов, подробные фото кают и палуб. Идеальное сочетание комфорта и увлекательных маршрутов для незабываемого отдыха на воде. +7 495 877-58-34.`,
  };

  const seaMeta = {
    title: `Круизный ${shipType(shipsInfoTitle, 'isTitleSeo')} ${
      seo.t_title
    } – морские круизы ${drawInfo.year}`,
    description: `Морские круизы на лайнере ${seo.t_title} купить онлайн по цене от ${minPrice} рублей. Узнать цены и скидки на навигацию ${drawInfo.year}, посмотреть расписание, фото и отзывы, услуги на борту, выбрать каюту и забронировать круиз онлайн или по тел: +7 499 643-57-40.
`,
  };

  const seoDataTitle = isSea ? seaMeta.title : meta.title;
  const seoDataDesc = isSea ? seaMeta.description : meta.description;

  let classCategoryReady;

  if (drawInfo.category_name === 'Люкс') {
    classCategoryReady = 'lux';
  } else if (drawInfo.category_name === 'Комфорт') {
    classCategoryReady = 'comfort';
  } else if (drawInfo.category_name === 'Стандарт') {
    classCategoryReady = 'standart';
  } else if (drawInfo.category_name === 'Эконом') {
    classCategoryReady = 'eco';
  } else if (drawInfo.category_name === 'Премиум') {
    classCategoryReady = 'premium';
  }

  // const sheme = drawInfo?.shema?.split('.')[0];

  // const arrayPhoto = drawInfo.like_ship.map(int =>
  //   int.photos.map(i =>
  //     drawInfo.ship_direction === 'sea'
  //       ? `https://sea.kruiz.online/assets/img/liners/liners/bergth_800x500/${i.file_name}`
  //       : `/images/cruiz_slider/ships/${int.id}/main/bergth_blogimg/${i.file_name}`,
  //   ),
  // );

  const transformedLikeShips = isSea
    ? drawInfo.like_ship.map(ship => ({
        ...ship,
        photos: ship.photos.map(photo => ({
          ...photo,
          src: `/assets/img/liners/liners/bergth_800x500/${photo.src.file_name}`,
          srcset: `/assets/img/liners/liners/bergth_800x500/${photo.srcset.file_name}`,
        })),
      }))
    : drawInfo.like_ship.map(ship => ({
        ...ship,
        photos: ship.photos.map(photo => ({
          ...photo,
          src: `/images/cruiz_slider/ships/${ship.id}/main/bergth_blogimg/${photo.src.file_name}`,
          srcset: `/images/cruiz_slider/ships/${ship.id}/main/bergth_blogimg/${photo.srcset.file_name}`,
          description: photo.src.description,
        })),
      }));

  const headerHeight = isMobile ? 124 : 148;
  const scrollTo = scrollToUtil(headerHeight);
  const elRef = useRef();
  const servicesRef = useRef();
  const badgeReviewsRef = useRef();

  const badgeClassRef = useRef();
  const pageRoute = [
    { title: 'Поиск теплоходов', link: 'https://kruiz.online/ships' },
    {
      title: `${shipType(shipsInfoTitle, 'isTitle')} ${drawInfo.t_title}`,
      link: '',
    },
  ];
  const seaPageRoute = [
    { title: 'Поиск лайнеров', link: 'https://kruiz.online/ships' },
    {
      title: `${shipType(shipsInfoTitle, 'isTitle')} ${drawInfo.t_title}`,
      link: '',
    },
  ];

  const [closeBanners, setCloseBanners] = useState(
    cookies?.closeBanner !== undefined || cookies?.closeBanner === 'false',
  );
  const currentDate = dayjs();
  const timeToCookie = currentDate.add(1, 'h');
  setCookie(null, 'closeBanner', 'false');
  const openBanner = () => {
    setCloseBanners(false);
    setCookie(null, 'closeBanner', 'false');
  };
  useHiddenWithCookie(openBanner);

  return (
    <SearchStoreProvider hydrationData={{ user, drawInfo }}>
      <RootStoreProvider
        hydrationData={{ user, drawInfo, discounts: drawInfo.discounts }}
      >
        <NextSeo
          title={seo?.seo_title ? seo.seo_title : seoDataTitle}
          description={seo?.description ? seo?.description : seoDataDesc}
          additionalLinkTags={[
            {
              rel: 'shortcut icon',
              href: '/favicon.ico?v=2',
            },
          ]}
          canonical={canonicalUrl}
        />
        <Layout
          isMobile={isMobile}
          isTablet={isTablet}
          shipsSea={isSea}
          isSea={isSea}
          showTopContent={!closeBanners}
          popularPopdirs={popularPopdirs}
          topContent={
            <div>
              <BannerTop
                isOpen={closeBanners}
                onOpen={setCloseBanners}
                timeToCookie={timeToCookie}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          }
        >
          <div className="container--block">
            <div>
              <div>
                <BreadCrumbs routeCrumbs={isSea ? seaPageRoute : pageRoute} />
              </div>
              <div className={styles.header}>
                <h1 className={styles.textWrapper}>
                  {seo?.h1 ||
                    `${shipType(shipsInfoTitle, 'isTitle')} ${
                      drawInfo.t_title
                    }`}
                </h1>
              </div>
              <div className={styles.allContent}>
                <div className={styles.imageShip}>
                  <div className={styles.topBar}>
                    <div className={styles.badgeContent}>
                      <div className={styles.badgeItemClass}>
                        <Button
                          view="text"
                          onClick={() => {
                            scrollTo(servicesRef);
                            YaAndGaShipPage('click_rate_ships');
                          }}
                        >
                          <Badge
                            view={classCategoryReady}
                            icon={classCategoryReady}
                            size={isMobile ? 'sm' : 'md'}
                          >
                            {drawInfo.category_name}
                          </Badge>
                        </Button>
                      </div>
                      <div className={styles.badgeItemClass}>
                        <Button view="text">
                          <Badge
                            view="rewies"
                            size={isMobile ? 'sm' : 'md'}
                            icon="desc"
                            onClick={() => YaAndGaShipPage('click_count_deck')}
                          >
                            {`${drawInfo.deck_count} ${declOfNum(
                              drawInfo.deck_count,
                              ['палуба', 'палубы', 'палуб'],
                            )}`}
                          </Badge>
                        </Button>
                      </div>
                      {drawInfo.review.length !== 0 && (
                        <div className={styles.badgeItemClass}>
                          <Button
                            view="text"
                            onClick={() => scrollTo(badgeReviewsRef)}
                          >
                            <Badge
                              view="shipRewievs"
                              icon="reviews"
                              size={isMobile ? 'sm' : 'md'}
                            >
                              {`${drawInfo.review_count_max}
                              ${declOfNum(drawInfo.review_count_max, [
                                'отзыв',
                                'отзыва',
                                'отзывов',
                              ])}`}
                            </Badge>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.galerey}>
                    <MainGallery
                      ship_id={drawInfo.id}
                      reviews={drawInfo.review}
                      maxi={drawInfo.ship_fotos.maxi}
                      mini={drawInfo.ship_fotos.mini}
                      isMobile={isMobile}
                      category={{
                        name: drawInfo.category_name,
                        type: classCategoryReady,
                      }}
                      title={drawInfo.t_title}
                      isSea={drawInfo.ship_direction === 'sea' && true}
                      shipPage
                      onArrowNext={() =>
                        YaAndGaShipPage('click_next_photo_in_modal_photo')
                      }
                      onArrowPrev={() =>
                        YaAndGaShipPage('click_previous_photo_in_modal_photo')
                      }
                      onImgClick={() =>
                        YaAndGaShipPage('click_photo_in_modal_photo')
                      }
                      onClickMore={() =>
                        YaAndGaShipPage('click_more_reviews_in_modal_photo')
                      }
                      onActionClose={() =>
                        YaAndGaShipPage('click_close_modal_photo')
                      }
                    />
                  </div>
                  <div>
                    <ShipInfo
                      years={drawInfo.restoration_date}
                      caute={drawInfo.cauta_count}
                      seat={drawInfo.seat_of_the_ship}
                      description={serialize(seo.seo_content)}
                      shipType={drawInfo.ship_direction}
                      shipHar={drawInfo.t_teh_xar}
                      construction={drawInfo.construction_date}
                      setClipp={setClipp}
                      clipp={clipp}
                      company={drawInfo.company}
                      onClickDescription={'click_open_description_ship'}
                      shipTypeInfo={shipType(shipsInfoTitle, 'isButton')}
                    />
                  </div>
                  <div className={styles.facilities}>
                    <Text variant="heading-4" component={'h2'}>
                      Услуги на борту
                    </Text>
                    <Features features={drawInfo.facilities} />
                  </div>
                </div>
                {minPrice !== 0 &&
                  drawCruise.cruises.cruises_data.length !== 0 && (
                    <div className={styles.pricePanel}>
                      <div className={styles.pricePanelTop}>
                        <PricePanel
                          cruizDetail={shipDetails}
                          sea={drawInfo.ship_direction}
                          price={drawInfo.kr_min_price}
                          onChoose={() => scrollTo(elRef)}
                          priceDiscounted={
                            drawInfo.kr_min_price_discounted ||
                            drawInfo.kr_min_price
                          }
                          isShips
                          elRef={badgeClassRef}
                          cashback={drawCruise.cashback}
                          ownerId={drawInfo.owner_id}
                          isMobile={isMobile}
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
          {drawCruise.cruises.cruises_data.length !== 0 && (
            <div ref={elRef} id={'filter'}>
              <FilterShips
                url={drawCruise.cruises.cruises_data[0].url}
                compact
                values
                isMobile={isMobile}
                sea={drawInfo.ship_direction}
                tabs={tabsYear}
                years={arrayYear}
                shipType={shipType(shipsInfoTitle, 'isRouter')}
                ownerId={drawInfo.owner_id}
              />
            </div>
          )}
          <div className="container container--start">
            {/*     {drawInfo.ship_direction === 'sea' && (
              <div className={styles.shema}>
                <div className={styles.shemaText}>
                  <Text variant="heading-4">
                    Схема палуб на{' '}
                    {drawInfo.ship_direction === 'sea'
                      ? 'лайнере'
                      : 'теплоходе'}{' '}
                    {drawInfo.t_title}
                  </Text>
                </div>
                <ModalSchemeShip
                  shipPage
                  riverSheme={drawInfo.ship_direction !== 'sea'}
                  schemeShip={sheme}
                  color="scheme"
                />
              </div>
            )} */}
            {drawInfo.yandex_zen_link && (
              <div className={styles.yandexButton}>
                <YandexLink link={drawInfo.yandex_zen_link} isShip />
              </div>
            )}
            {drawInfo.review.length > 0 && (
              <section className={styles.sect}>
                <div className={styles.reviewsHeader} ref={badgeReviewsRef}>
                  <Text variant="heading-1" component="h2">
                    Отзывы
                  </Text>
                </div>
                <Reviews
                  items={drawInfo.review}
                  isMobile={isMobile}
                  onActionClick={'click_view_more_reviews'}
                />
              </section>
            )}
            {drawInfo.like_ship.length > 0 && (
              <div className={styles.shema}>
                <Text variant="heading-4">Вам так же могут понравиться</Text>
                <div className={styles.cards}>
                  {transformedLikeShips.map((i, id) => (
                    <div className={styles.cardShips}>
                      <Card
                        isShipCard
                        key={id}
                        title={i.t_title}
                        category={i.category_id}
                        countDesc={i.deck_count}
                        caute_count={i.cauta_count}
                        seat_count={i.seat_of_the_ship}
                        images={i.photos}
                        link={i.url}
                        blackFriday={0}
                        shipId={i.id}
                        isMobile={isMobile}
                        sea={isSea}
                        recomend={isSea}
                      />
                    </div>
                  ))}
                </div>
                <div className={styles.footerCard}>
                  <Anchor
                    variant="showMore"
                    href={isSea ? '/liners' : '/ships'}
                    onClick={() => {
                      YaAndGaShipPage('click_view_more_you_might_like');
                    }}
                  >
                    Показать ещё
                  </Anchor>
                </div>
              </div>
            )}
            {hasSeo(seo) && (
              <section className={styles.seoTabs}>
                <Collapsed seo={seoData(seo)} shorter isSeo />
              </section>
            )}
          </div>
        </Layout>
        <ModalReload />
      </RootStoreProvider>
    </SearchStoreProvider>
  );
}
export const getServerSideProps = async ctx => {
  const cookies = nookies.get(ctx);
  const { token } = cookies;

  // Получаем только путь без query параметров
  const canonicalPath = ctx.req.url.split('?')[0];
  const canonicalUrl = `https://kruiz.online${canonicalPath}`;

  for (let i = 0; i < redirectInvalidUrlShips.length; i++) {
    if (ctx.query.id === redirectInvalidUrlShips[i].from) {
      return {
        redirect: {
          destination: redirectInvalidUrlShips[i].destination,
          permanent: true,
        },
      };
    }
  }

  // Энкодим русские буквы для корректной работы ручки
  const encodedId = encodeURI(ctx.query.id);
  // Проверяем на цифры для корректной работы ручки
  const regExpId = /^\d+$/;

  // Проверяем на наличие цифр в ID
  if (ctx.query.id?.search(regExpId) !== -1) {
    return {
      redirect: {
        destination: '/404',
        permanent: true,
      },
    };
  }

  try {
    // Получаем shipPromise и drawInfo
    const shipPromise = await apiProxy
      .get(`ApiShips/ship_info/${encodedId}?echo_json=1`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      })
      .then(({ data }) => data)
      .catch(error => {
        logger({
          additionalFields: {
            url: ctx.req.url,
            page_id: ctx.query.id,
          },
          error,
          level: 'error',
          message: `${
            error.response?.statusText || 'ships page was not created'
          }`,
        });
        redirectFromTo({ url: '', code: error.response?.status || 404 }, ctx);
      });

    if (!shipPromise) {
      // Обработка ошибки или перенаправление
    }

    if (shipPromise?.redirect.code !== 200) {
      return redirectFromTo(shipPromise?.redirect, ctx);
    }

    const drawInfo = shipPromise;

    // Выполняем новый API-запрос с использованием drawInfo.is_sea_ship
    const popularPopdirsPromise = await apiProxy
      .get(`ApiPopdirNew/getHeaderPopdir/${drawInfo.is_sea_ship}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      })
      .then(({ data }) => data)
      .catch(console.log);

    // Выполняем остальные запросы параллельно
    const [userPromises, cruisePromise, allShipPromise] =
      await Promise.allSettled([
        apiProxy
          .get(`ApiUsers/check_token/${token ?? ''}`, {
            headers: {
              'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
              ym_uid: cookies._ym_uid || 'ym_cid not found',
            },
          })
          .then(({ data }) => data)
          .catch(console.log),
        apiProxy
          .get(`ApiShips/ship_cruises/${ctx.query.id}`, {
            headers: {
              'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
              ym_uid: cookies._ym_uid || 'ym_cid not found',
            },
          })
          .then(({ data }) => data)
          .catch(console.log),
        apiProxy
          .get(`ApiShips/get_ships?id=${shipPromise.id}`, {
            headers: {
              'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
              ym_uid: cookies._ym_uid || 'ym_cid not found',
            },
          })
          .then(({ data }) => data)
          .catch(console.log),
      ]);

    const user =
      userPromises.status === 'fulfilled' ? userPromises.value : null;
    const drawCruise =
      cruisePromise.status === 'fulfilled' ? cruisePromise.value : null;
    const seo =
      allShipPromise.status === 'fulfilled' ? allShipPromise.value : null;

    logger({
      additionalFields: {
        url: ctx.req.url,
        page_id: ctx.query.id,
      },
      level: 'debug',
      ym_uid: cookies._ym_uid,
      message: 'ships page has been created',
    });

    return {
      props: {
        store: { user, cookies, popularPopdirs: popularPopdirsPromise ?? null },
        uaString: ctx.req.headers['user-agent'],
        drawInfo,
        seo,
        drawCruise,
        canonicalUrl,
      },
    };
  } catch (error) {
    logger({
      additionalFields: {
        url: ctx.req.url,
        page_id: ctx.query.id,
      },
      error,
      level: 'error',
      message: `${error.response?.statusText || 'ships page was not created'}`,
    });
    return redirectFromTo(
      { url: '', code: error.response?.status || 404 },
      ctx,
    );
  }
};
